import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Save, User as UserIcon, AlertCircle, FileText, Plus } from "lucide-react";
import { userHasVerifiedTokens } from "@/utils/blogUtils";
import { useConversation } from "@/hooks/useConversation";
import { AboutOwnerProfile } from "@/components/about/AboutOwnerProfile";
import { AvatarSelector } from "@/components/profile/AvatarSelector";
import { savePendingChange, getPendingChanges, saveProfileCache, getProfileCache } from '@/utils/offlineStorage';
import { syncPendingChanges, setupAutoSync, isOnline } from '@/utils/syncService';
import { ProfileFormFields } from "@/components/profile/ProfileFormFields";
import { TokenVerification } from "@/components/profile/TokenVerification";
import { SocialFields } from "@/components/profile/SocialFields";
import { SearchHistory } from "@/components/profile/SearchHistory";
import { useDebouncedNSFWCheck } from "@/hooks/useDebouncedValidation";
import { useMetefyEasterEgg } from "@/hooks/useMetefyEasterEgg";

function getOwnerProfileFromPreviewResult(previewResult: any): any {
  return {
    id: previewResult.id || '',
    first_name: previewResult.first_name ?? '',
    last_name: previewResult.last_name ?? '',
    x_username: previewResult.x_username ?? '',
    bio: typeof previewResult.bio === 'string' ? previewResult.bio : '',
    mask_bio: previewResult.mask_bio ?? false,
    metro_card_number: previewResult.metro_card_number ?? '',
    vehicle_number: previewResult.vehicle_number ?? '',
    created_at: previewResult.created_at ?? '',
    updated_at: previewResult.updated_at ?? '',
    show_email: previewResult.show_email ?? false,
    show_mobile: previewResult.show_mobile ?? false,
    metefy_token: previewResult.metefyToken || previewResult.metefy_token || '',
    photo_url: previewResult.photo_url ?? '',
    social_sms: previewResult.social_sms ?? '',
    social_call: previewResult.social_call ?? '',
    social_telegram: previewResult.social_telegram ?? '',
    social_whatsapp: previewResult.social_whatsapp ?? '',
    use_social_icons: previewResult.use_social_icons ?? true,
    show_only_filled_socials: previewResult.show_only_filled_socials ?? false,
  };
}

export default function Profile() {
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<{
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    x_username: string;
    bio: string;
    mask_bio?: boolean;
    mobile_number?: string;
    metro_card_number: string;
    vehicle_number: string;
    dateAdded: string;
    photo: string;
    photo_url: string;
    social_sms: string;
    social_call: string;
    social_telegram: string;
    social_whatsapp: string;
    use_social_icons: boolean;
  }>({
    name: "",
    first_name: "",
    last_name: "",
    email: "",
    x_username: "",
    bio: "",
    mask_bio: false,
    mobile_number: "",
    metro_card_number: "",
    vehicle_number: "",
    dateAdded: "",
    photo: "",
    photo_url: "",
    social_sms: '',
    social_call: '',
    social_telegram: '',
    social_whatsapp: '',
    use_social_icons: true,
  });
  
  const [metefyTokens, setMetefyTokens] = useState<Array<{
    token: string;
    password?: string;
    dateAdded: string;
    name?: string | null;
    call?: string | null;
    whatsapp?: string | null;
    x_username?: string | null;
    id?: string;
  }>>([]);

  // Store the original metro card number for change detection
  const [originalMetroCard, setOriginalMetroCard] = useState<string>("");
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [previewResult, setPreviewResult] = useState<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    mobile: string | null;
    x_username: string | null;
    metro_card_number: string | null;
    vehicle_number: string | null;
    show_email: boolean;
    show_mobile: boolean;
    metefyToken: string | null;
    bio?: string | null;
    created_at?: string;
    updated_at?: string;
    photo_url?: string | null;
    social_sms?: string;
    social_call?: string;
    social_telegram?: string;
    social_whatsapp?: string;
    use_social_icons?: boolean;
    show_only_filled_socials?: boolean;
  } | null>(null);
  const [totalSearches, setTotalSearches] = useState<number>(0);
  const [uniqueSearches, setUniqueSearches] = useState<number>(0);
  const [useMobileForAllSocials, setUseMobileForAllSocials] = useState(false);
  const [searchHistory, setSearchHistory] = useState<Array<{
    id: string;
    search_type: string;
    search_value: string;
    searched_at: string;
    latitude?: number | null;
    longitude?: number | null;
    city?: string | null;
    address?: string | null;
    country?: string | null;
    region?: string | null;
  }>>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [canWriteBlog, setCanWriteBlog] = useState(false);
  // Optimized hooks for validation and easter egg
  const { checkNSFW } = useDebouncedNSFWCheck();
  const { triggerMetefyEasterEgg } = useMetefyEasterEgg();
  
  // Array of background images to randomly select from (using the same as AboutOwnerProfile)
  const backgroundImages = [
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760997986/Untitled_design_21_qq7mzf.png", // Image 21
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1761001501/Untitled_design_23_aukr7e.png"  // Image 23
  ];
  
  const [selectedImage, setSelectedImage] = useState(backgroundImages[0]);
  const { createOrGetConversation, isLoading: isCreatingConversation } = useConversation();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (user && window.location.pathname === "/") {
      navigate("/profile", { replace: true });
    }
  }, [user, navigate]);

  // Check admin access
  useEffect(() => {
    (async () => {
      if (!user) { setIsAdmin(false); return; }
      try {
        const { data } = await supabase
          .from('admin_users')
          .select('user_id')
          .eq('user_id', user.id)
          .maybeSingle();
        setIsAdmin(!!data);
      } catch {
        setIsAdmin(false);
      }
    })();
  }, [user]);

  // Initialize offline storage and setup auto-sync
  useEffect(() => {
    if (!user) return;

    // Initialize offline storage
    import('@/utils/offlineStorage').then(({ initOfflineStorage }) => {
      initOfflineStorage();
    });

    // Setup auto-sync
    const cleanup = setupAutoSync(user.id, async (status) => {
      setIsSyncing(status.isSyncing);
      setPendingChangesCount(status.pendingCount);
      // Only show toast if changes were actually synced
      if (status.changesSynced > 0 && status.pendingCount === 0) {
        toast({
          title: "Changes Synced",
          description: "Your offline changes have been synced successfully.",
        });
        // Refresh profile data after sync by reloading the page
        // This ensures all data is fresh from the database
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    });

    // Check pending changes
    const checkPending = async () => {
      const changes = await getPendingChanges(user.id);
      setPendingChangesCount(changes.length);
    };
    checkPending();

    // Listen for online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      checkPending();
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      cleanup();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user, toast]);

  // Periodically check pending changes
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(async () => {
      const changes = await getPendingChanges(user.id);
      setPendingChangesCount(changes.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [user]);

  // Randomly select an image when the preview dialog opens
  useEffect(() => {
    if (previewDialogOpen) {
      const randomIndex = Math.floor(Math.random() * backgroundImages.length);
      setSelectedImage(backgroundImages[randomIndex]);
    }
  }, [previewDialogOpen]);

  // Handle back button to close dialog
  useEffect(() => {
    if (!previewDialogOpen) return;
    const onPopState = (e: PopStateEvent) => {
      setPreviewDialogOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    window.history.pushState(null, '', window.location.href);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [previewDialogOpen]);

  // Function to apply pending changes to profile state
  const applyPendingChanges = async (baseProfile: any, userId: string) => {
    const pendingChanges = await getPendingChanges(userId);
    let updatedProfile = { ...baseProfile };
    
    // Apply all pending changes in order
    for (const change of pendingChanges) {
      if (change.type === 'profile') {
        updatedProfile = { ...updatedProfile, ...change.data };
      } else if (change.type === 'avatar') {
        updatedProfile.photo_url = change.data.photo_url;
      }
    }
    
    return updatedProfile;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!user) return;
        
        // Check if offline - load from cache
        if (!navigator.onLine) {
          const cached = await getProfileCache(user.id);
          if (cached) {
            let data = cached.profileData;
            
            // Apply pending changes to cached data
            data = await applyPendingChanges(data, user.id);
            
            const avatarUrl = data.photo_url || user.user_metadata?.avatar_url || getRandomAvatar(user.id);
            
            setProfile({
              name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
              first_name: data.first_name || '',
              last_name: data.last_name || '',
              email: user.email || '',
              x_username: data.x_username || '',
              bio: data.bio || '',
              mask_bio: (data as any).mask_bio ?? false,
              mobile_number: (data as any).mobile_number || '',
              metro_card_number: data.metro_card_number || '',
              vehicle_number: (data as any).vehicle_number || '',
              dateAdded: data.created_at ? new Date(data.created_at).toISOString().split('T')[0] : '',
              photo: '',
              photo_url: avatarUrl,
              social_sms: (data.social_sms ?? ''),
              social_call: (data.social_call ?? ''),
              social_telegram: (data.social_telegram ?? ''),
              social_whatsapp: (data.social_whatsapp ?? ''),
              use_social_icons: data.use_social_icons ?? true,
            });
            setOriginalMetroCard(data.metro_card_number || "");
            setTotalSearches((data as any).total_searches || 0);
            setUniqueSearches((data as any).unique_searches || 0);
            
            if (cached.tokens && cached.tokens.length > 0) {
              setMetefyTokens(cached.tokens);
            }
            
            setIsLoading(false);
            return;
          } else {
            // No cache available offline
            setIsLoading(false);
            return;
          }
        }
        
        // Online - fetch from Supabase
        const { data, error } = await supabase.from('profiles').select('*').eq('id', user.id).single();
        if (error) throw error;
        
        if (data) {
          // Apply pending changes to profile data
          let profileData = await applyPendingChanges(data, user.id);
          
          // Prioritize saved photo_url from database, then Google picture, then random avatar
          const avatarUrl = profileData.photo_url || user.user_metadata?.avatar_url || getRandomAvatar(user.id);
          
          setProfile({
            name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim(),
            first_name: profileData.first_name || '',
            last_name: profileData.last_name || '',
            email: user.email || '',
            x_username: profileData.x_username || '',
            bio: profileData.bio || '',
            mask_bio: (profileData as any).mask_bio ?? false,
            mobile_number: (profileData as any).mobile_number || '',
            metro_card_number: profileData.metro_card_number || '',
            vehicle_number: (profileData as any).vehicle_number || '',
            dateAdded: profileData.created_at ? new Date(profileData.created_at).toISOString().split('T')[0] : '',
            photo: '',
            photo_url: avatarUrl,
            social_sms: (profileData.social_sms ?? ''),
            social_call: (profileData.social_call ?? ''),
            social_telegram: (profileData.social_telegram ?? ''),
            social_whatsapp: (profileData.social_whatsapp ?? ''),
            use_social_icons: profileData.use_social_icons ?? true,
          });
          setOriginalMetroCard(profileData.metro_card_number || "");
          // All-time searches from profiles
          // @ts-ignore - column exists in DB
          setTotalSearches((profileData as any).total_searches || 0);
          // @ts-ignore - column exists in DB
          setUniqueSearches((profileData as any).unique_searches || 0);
          
          // Cache profile data (cache original, not with pending changes)
          await saveProfileCache(user.id, data);
        }

        const { data: metefyData, error: metefyError } = await supabase
          .from('metefy_tokens')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_verified', true)
          .order('created_at', { ascending: false });
          
        if (metefyError) throw metefyError;
        if (metefyData && metefyData.length > 0) {
          const formattedTokens = metefyData.map(token => ({
            token: token.token,
            password: token.password,
            dateAdded: new Date(token.created_at).toISOString().split('T')[0],
            name: token.name || null,
            call: token.call || null,
            whatsapp: token.whatsapp || null,
            x_username: token.x_username || null,
            id: token.id
          }));
          setMetefyTokens(formattedTokens);
          
          // Cache tokens
          if (data) {
            await saveProfileCache(user.id, data, formattedTokens);
          }
        } else {
          setMetefyTokens([]);
          // Cache empty tokens
          if (data) {
            await saveProfileCache(user.id, data, []);
          }
        }
      } catch (error: any) {
        // If online fetch fails, try cache
        if (navigator.onLine) {
            const cached = await getProfileCache(user.id);
            if (cached) {
              let data = cached.profileData;
              
              // Apply pending changes to cached data
              data = await applyPendingChanges(data, user.id);
              
              const avatarUrl = data.photo_url || user.user_metadata?.avatar_url || getRandomAvatar(user.id);
              
              setProfile({
                name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
                first_name: data.first_name || '',
                last_name: data.last_name || '',
                email: user.email || '',
                x_username: data.x_username || '',
                bio: data.bio || '',
                mask_bio: (data as any).mask_bio ?? false,
                mobile_number: (data as any).mobile_number || '',
                metro_card_number: data.metro_card_number || '',
                vehicle_number: (data as any).vehicle_number || '',
                dateAdded: data.created_at ? new Date(data.created_at).toISOString().split('T')[0] : '',
                photo: '',
                photo_url: avatarUrl,
                social_sms: (data.social_sms ?? ''),
                social_call: (data.social_call ?? ''),
                social_telegram: (data.social_telegram ?? ''),
                social_whatsapp: (data.social_whatsapp ?? ''),
                use_social_icons: data.use_social_icons ?? true,
              });
              setOriginalMetroCard(data.metro_card_number || "");
              setTotalSearches((data as any).total_searches || 0);
              setUniqueSearches((data as any).unique_searches || 0);
              
              if (cached.tokens && cached.tokens.length > 0) {
                setMetefyTokens(cached.tokens);
              }
            } else {
            toast({
              title: "Error fetching profile",
              description: error.message,
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "Error fetching profile",
            description: error.message,
            variant: "destructive"
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
    
    // Check if user can write blogs
    const checkCanWriteBlog = async () => {
      if (!user) {
        setCanWriteBlog(false);
        return;
      }
      const hasTokens = await userHasVerifiedTokens(user.id);
      setCanWriteBlog(hasTokens);
    };
    checkCanWriteBlog();
    // Don't fetch search history on page load - only when user clicks "Show Search History"
  }, [user, toast]);

  const fetchSearchHistory = async () => {
    if (!user) return;
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('search_history')
        .select('*')
        .eq('user_id', user.id)
        .order('searched_at', { ascending: false })
        .limit(20); // Only fetch last 20 searches
      
      if (error) throw error;
      setSearchHistory(data || []);
    } catch (error: any) {
      console.error('Failed to fetch search history:', error);
      toast({
        title: "Failed to load search history",
        description: "Could not load search history. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleShowSearchHistory = () => {
    setShowSearchHistory(true);
    if (searchHistory.length === 0) {
      fetchSearchHistory();
    }
  };

  const getRandomAvatar = (userId: string) => {
    const hash = hashString(userId);
    const avatarStyles = ['adventurer', 'adventurer-neutral', 'avataaars', 'big-ears', 'bottts', 'croodles', 'micah'];
    const style = avatarStyles[hash % avatarStyles.length];
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${userId}`;
  };

  const hashString = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  // Optimized handlers - separated by field type for better performance
  const handleNameChange = useCallback((value: string) => {
    // Sanitize: remove HTML tags, <>, and disallow symbols except @ and _
    let sanitized = value.replace(/<[^>]*>/g, '').replace(/[<>]/g, '');
    sanitized = sanitized.replace(/[^a-zA-Z0-9@_\s]/g, '');
    
    // Check NSFW (debounced, won't block typing)
    if (checkNSFW(sanitized, 'name')) {
      return; // Don't update if NSFW detected
    }
    
    // Trigger easter egg check (debounced)
    triggerMetefyEasterEgg('name', sanitized);
    
    const [firstName = '', ...lastNameParts] = sanitized.split(' ');
    const lastName = lastNameParts.join(' ');
    setProfile(prev => ({
      ...prev,
      name: sanitized,
      first_name: firstName,
      last_name: lastName
    }));
  }, [checkNSFW, triggerMetefyEasterEgg]);

  const handleXUsernameChange = useCallback((value: string) => {
    // Allow only a-z, A-Z, 0-9, _, max 16 chars, no spaces
    let sanitized = value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 16);
    
    if (checkNSFW(sanitized, 'x_username')) {
      return;
    }
    
    triggerMetefyEasterEgg('x_username', sanitized);
    
    setProfile(prev => ({
      ...prev,
      x_username: sanitized
    }));
  }, [checkNSFW, triggerMetefyEasterEgg]);

  const handleBioChange = useCallback((value: string) => {
    // Allow letters, numbers, spaces, @, _, ., :, , and emojis
    let sanitized = value.replace(/<[^>]*>/g, '').replace(/[<>]/g, '');
    
    if (checkNSFW(sanitized, 'bio')) {
      return;
    }
    
    triggerMetefyEasterEgg('bio', sanitized);
    
    setProfile(prev => ({
      ...prev,
      bio: sanitized
    }));
  }, [checkNSFW, triggerMetefyEasterEgg]);

  const handleMobileChange = useCallback((value: string) => {
    setProfile(prev => {
      const newProfile = { ...prev, mobile_number: value };
      if (useMobileForAllSocials) {
        const onlyDigits = value.replace(/\D/g, "");
        newProfile.social_call = onlyDigits;
        newProfile.social_telegram = onlyDigits;
        newProfile.social_whatsapp = onlyDigits;
      }
      return newProfile;
    });
  }, [useMobileForAllSocials]);

  const handleMetroCardChange = useCallback((value: string) => {
    setProfile(prev => ({ ...prev, metro_card_number: value }));
  }, []);

  const handleVehicleNumberChange = useCallback((value: string) => {
    setProfile(prev => ({ ...prev, vehicle_number: value }));
  }, []);

  const handleSocialChange = useCallback((field: string, value: string) => {
    if (field === 'social_sms' || field === 'social_call' || field === 'social_whatsapp') {
      // Numeric-only socials, limit to 15 digits
      const sanitized = value.replace(/\D/g, '').slice(0, 15);
      setProfile(prev => ({ ...prev, [field]: sanitized }));
    } else if (field === 'social_telegram') {
      // Allow @username (a-zA-Z0-9_), or digits/plus; strip spaces; limit length
      let raw = value.replace(/\s+/g, '').slice(0, 32);
      const prefix = raw.startsWith('@') || raw.startsWith('+') ? raw[0] : '';
      let rest = raw.slice(prefix ? 1 : 0).replace(/[^a-zA-Z0-9_]/g, '');
      let sanitized = prefix + rest;
      triggerMetefyEasterEgg('social_telegram', sanitized);
      setProfile(prev => ({ ...prev, social_telegram: sanitized }));
    }
  }, [triggerMetefyEasterEgg]);

  // Token verification handlers - moved to TokenVerification component
  const handleTokensUpdate = useCallback((tokens: Array<{
    token: string;
    password?: string;
    dateAdded: string;
    name?: string | null;
    call?: string | null;
    whatsapp?: string | null;
    x_username?: string | null;
    id?: string;
  }>) => {
    setMetefyTokens(tokens);
  }, []);

  const handleProfileUpdateFromToken = useCallback(async (updates: any) => {
    if (!user) return;
    
    const { error: profileError } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);
    
    if (!profileError) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileData) {
        setProfile(prev => ({
          ...prev,
          first_name: profileData.first_name || prev.first_name,
          last_name: profileData.last_name || prev.last_name,
          name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || prev.name,
          x_username: profileData.x_username || prev.x_username,
          social_call: (profileData as any).social_call || prev.social_call,
          social_whatsapp: (profileData as any).social_whatsapp || prev.social_whatsapp,
          bio: profileData.bio || prev.bio,
          photo_url: profileData.photo_url || prev.photo_url
        }));
      }
    }
  }, [user]);

  const handleAvatarSelect = async (avatarUrl: string, source: 'dicebear' | 'google') => {
    if (!user) return;
    
    try {
      if (isOnline()) {
        const { error } = await supabase
          .from('profiles')
          .update({ photo_url: avatarUrl })
          .eq('id', user.id);
        
        if (error) throw error;
        
        setProfile(prev => ({ ...prev, photo_url: avatarUrl }));
        setShowAvatarSelector(false);
        toast({
          title: "Avatar Updated",
          description: "Your profile picture has been updated successfully."
        });
        
        // Refresh profile cache after successful update
        const { data: updatedProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (updatedProfile) {
          const { data: metefyData } = await supabase
            .from('metefy_tokens')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_verified', true)
            .order('created_at', { ascending: false });
          
          const formattedTokens = metefyData?.map(token => ({
            token: token.token,
            password: token.password,
            dateAdded: new Date(token.created_at).toISOString().split('T')[0],
            name: token.name || null,
            call: token.call || null,
            whatsapp: token.whatsapp || null,
            x_username: token.x_username || null,
            id: token.id
          })) || [];
          
          await saveProfileCache(user.id, updatedProfile, formattedTokens);
        }
      } else {
        // Save offline
        await savePendingChange(user.id, 'avatar', { photo_url: avatarUrl });
        setProfile(prev => ({ ...prev, photo_url: avatarUrl }));
        setShowAvatarSelector(false);
        toast({
          title: "Avatar Saved Offline",
          description: "Your avatar will be updated when you're back online.",
        });
        const changes = await getPendingChanges(user.id);
        setPendingChangesCount(changes.length);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update avatar."
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (profile.metro_card_number && profile.metro_card_number.length < 4) {
      toast({
        title: "Metro Card Number Too Short",
        description: "Metro card number must be at least 4 digits.",
        variant: "destructive"
      });
      return;
    }
    
    // Validate vehicle number if provided (should be alphanumeric, typically 6-15 characters)
    if (profile.vehicle_number && profile.vehicle_number.trim().length > 0) {
      const vehicleNumber = profile.vehicle_number.trim().toUpperCase();
      if (vehicleNumber.length < 6 || vehicleNumber.length > 15) {
        toast({
          title: "Invalid Vehicle Number",
          description: "Vehicle number must be between 6 and 15 characters.",
          variant: "destructive"
        });
        return;
      }
    }
    try {
      const mobileNumberValue = (profile.mobile_number || '').trim();
      const numericMobile = mobileNumberValue.replace(/\D/g, '').slice(0, 15);
      const trimmedSocialCall = (profile.social_call || '').trim();
      const trimmedSocialWhatsapp = (profile.social_whatsapp || '').trim();
      const finalSocialCall = trimmedSocialCall || (numericMobile ? numericMobile : '');
      const finalSocialWhatsapp = trimmedSocialWhatsapp || (numericMobile ? numericMobile : '');
      const metroCardValue = profile.metro_card_number === '' ? null : profile.metro_card_number;
      const vehicleNumberValue = profile.vehicle_number === '' ? null : profile.vehicle_number.trim().toUpperCase();
      let updateObj: any = {
        first_name: profile.first_name,
        last_name: profile.last_name,
        x_username: profile.x_username,
        bio: profile.bio,
        mask_bio: !!profile.mask_bio,
        metro_card_number: metroCardValue,
        vehicle_number: vehicleNumberValue,
        mobile_number: profile.mobile_number,
        social_sms: profile.social_sms,
        social_call: finalSocialCall,
        social_telegram: profile.social_telegram,
        social_whatsapp: finalSocialWhatsapp,
        use_social_icons: profile.use_social_icons,
      };
      // If the metro card number changed, update created_at to today
      if (typeof profile.metro_card_number === 'string' && profile.metro_card_number !== originalMetroCard) {
        updateObj.created_at = new Date().toISOString();
      }

      // Check if online
      if (isOnline()) {
        // Try to save online
        const { error } = await supabase.from('profiles').update(updateObj).eq('id', user.id);
        
        if (error) {
          if (error.code === '23505' && error.message.includes('duplicate key value')) {
            if (error.message.includes('metro_card_number')) {
              toast({
                title: "Metro Card Already Registered",
                description: "This metro card number is already registered to another account.",
                variant: "destructive"
              });
              return;
            }
            if (error.message.includes('vehicle_number')) {
              toast({
                title: "Vehicle Number Already Registered",
                description: "This vehicle number is already registered to another account.",
                variant: "destructive"
              });
              return;
            }
          }
          throw error;
        }
        
        toast({
          title: "Profile updated",
          description: "Your profile information has been updated successfully."
        });
        
        // Refresh profile cache after successful update
        const { data: updatedProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (updatedProfile) {
          const { data: metefyData } = await supabase
            .from('metefy_tokens')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_verified', true)
            .order('created_at', { ascending: false });
          
          const formattedTokens = metefyData?.map(token => ({
            token: token.token,
            password: token.password,
            dateAdded: new Date(token.created_at).toISOString().split('T')[0],
            name: token.name || null,
            call: token.call || null,
            whatsapp: token.whatsapp || null,
            x_username: token.x_username || null,
            id: token.id
          })) || [];
          
          await saveProfileCache(user.id, updatedProfile, formattedTokens);
        }
      } else {
        // Save offline
        await savePendingChange(user.id, 'profile', updateObj);
        toast({
          title: "Saved Offline",
          description: "Your changes have been saved locally and will sync when you're back online.",
        });
        const changes = await getPendingChanges(user.id);
        setPendingChangesCount(changes.length);
      }
      setIsEditing(false);
      if (!trimmedSocialCall || !trimmedSocialWhatsapp) {
        setProfile(prev => ({
          ...prev,
          social_call: finalSocialCall,
          social_whatsapp: finalSocialWhatsapp,
        }));
      }
      // Update the original metro card number if it changed
      if (typeof profile.metro_card_number === 'string' && profile.metro_card_number !== originalMetroCard) {
        setOriginalMetroCard(profile.metro_card_number);
        setProfile(prev => ({ ...prev, dateAdded: new Date().toISOString().split('T')[0] }));
      }
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive"
      });
    }
  };


  const handleMessageClick = async () => {
    if (!previewResult?.id) return;
    
    try {
      const result = await createOrGetConversation({
        id: previewResult.id,
        name: `${previewResult.first_name || ''} ${previewResult.last_name || ''}`.trim() || 'User',
        photo: previewResult.photo_url || '/default-avatar.png'
      });
      
      if (result) {
        setPreviewDialogOpen(false); // Close dialog first
        
        // Navigate and ensure event is dispatched after navigation is complete
        navigate('/messaging', {
          state: {
            openConversation: {
              contact: result.contact,
              conversationId: result.conversationId
            }
          }
        });
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast({
        title: "Error",
        description: "Failed to open conversation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handlePreview = async () => {
    // Fetch show_only_filled_socials from database for preview
    let showOnlyFilledSocials = false;
    if (user) {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('show_only_filled_socials')
          .eq('id', user.id)
          .single();
        if (data) {
          showOnlyFilledSocials = !!(data as any).show_only_filled_socials;
        }
      } catch (error) {
        // Silently fail, use default
      }
    }
    
    // Use local profile state to show preview - no Supabase fetch needed
    // This allows users to preview even without metro card or token
    const metefyToken = metefyTokens.length > 0 ? metefyTokens[0].token : null;
    
    setPreviewResult({
      id: user?.id || '',
      first_name: profile.first_name || null,
      last_name: profile.last_name || null,
      x_username: profile.x_username || null,
      bio: profile.bio || null,
      metro_card_number: profile.metro_card_number || null,
      vehicle_number: profile.vehicle_number || null,
      created_at: profile.dateAdded || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      show_email: false, // Default to false for preview
      show_mobile: false, // Default to false for preview
      metefyToken: metefyToken,
      photo_url: profile.photo_url || null,
      // Pass actual values from profile state
      // AboutOwnerProfile checks: useUserSocials && profile?.social_sms ? profile.social_sms : DEFAULT
      // Ensure we pass the actual string values (convert to string if needed, but preserve non-empty values)
      social_sms: profile.social_sms ? String(profile.social_sms) : '',
      social_call: profile.social_call ? String(profile.social_call) : '',
      social_telegram: profile.social_telegram ? String(profile.social_telegram) : '',
      social_whatsapp: profile.social_whatsapp ? String(profile.social_whatsapp) : '',
      use_social_icons: profile.use_social_icons ?? true,
      show_only_filled_socials: showOnlyFilledSocials,
      email: null,
      mobile: null,
    });
    
    setPreviewDialogOpen(true);
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* My Profile Header (sticky) */}
      <div className="w-full py-2 flex justify-between items-center md:border-none sticky top-0 z-10 bg-background/95 backdrop-blur-sm">
          <div className="flex items-baseline gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">My Profile</h1>
            {isAdmin && (
              <button
                onClick={() => navigate('/adminlookup')}
                className="text-foreground/80 hover:text-foreground inline-flex items-baseline"
                aria-label="Open Admin Lookup"
                title="Admin Lookup"
              >
                <UserIcon size={18} className="mb-0.5 md:mb-0" />
              </button>
            )}
          </div>
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)} className="border-border text-foreground hover:bg-muted">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
                <Save size={16} />
                Save
              </Button>
            </div>
          )}
      </div>
      
      <div className="mt-4 md:mt-6 px-0">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <Card className="bg-card border border-border shadow-sm mb-6 p-6">
            {/* Combined Offline/Pending Changes Indicator */}
            {(isOffline || pendingChangesCount > 0) && (
              <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
                isOffline 
                  ? 'bg-yellow-500/10 border border-yellow-500/20' 
                  : 'bg-blue-500/10 border border-blue-500/20'
              }`}>
                <div className="flex items-center gap-2">
                  {isOffline ? (
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <Loader2 className={`w-5 h-5 text-blue-500 ${isSyncing ? 'animate-spin' : ''}`} />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      isOffline ? 'text-yellow-500' : 'text-blue-500'
                    }`}>
                      {isOffline 
                        ? 'You are offline' 
                        : isSyncing 
                          ? 'Syncing...' 
                          : `${pendingChangesCount} pending change${pendingChangesCount > 1 ? 's' : ''} will be synced when online`}
                    </p>
                    {isOffline && pendingChangesCount > 0 && (
                      <p className="text-xs text-yellow-500/80 mt-1">
                        {pendingChangesCount} change{pendingChangesCount > 1 ? 's' : ''} will be synced when you're back online
                      </p>
                    )}
                    {!isOffline && !isSyncing && pendingChangesCount > 0 && (
                      <p className="text-xs text-blue-500/80">
                        Changes will sync automatically when you're back online
                      </p>
                    )}
                  </div>
                </div>
                {!isSyncing && isOnline() && pendingChangesCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setIsSyncing(true);
                      const status = await syncPendingChanges(user.id);
                      const changes = await getPendingChanges(user.id);
                      setPendingChangesCount(changes.length);
                      setIsSyncing(false);
                      if (status.changesSynced > 0) {
                        // Refresh profile after sync
                        window.location.reload();
                      }
                    }}
                    className="border-border text-foreground hover:bg-muted"
                  >
                    Sync Now
                  </Button>
                )}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex flex-col items-center gap-4">
                <Avatar 
                  className="w-32 h-32 border-2 border-primary cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowAvatarSelector(true)}
                  title="Click to change avatar"
                >
                  <AvatarImage src={profile.photo_url} />
                  <AvatarFallback className="text-3xl bg-primary text-primary-foreground">
                    {profile.first_name ? profile.first_name.charAt(0) : '?'}
                  </AvatarFallback>
                </Avatar>
                <p className="text-xs text-foreground/80 text-center">Click to change</p>
              </div>
              
              <ProfileFormFields
                profile={profile}
                isEditing={isEditing}
                onNameChange={handleNameChange}
                onXUsernameChange={handleXUsernameChange}
                onBioChange={handleBioChange}
                onMobileChange={handleMobileChange}
                onMetroCardChange={handleMetroCardChange}
                onVehicleNumberChange={handleVehicleNumberChange}
              />
            </div>
          </Card>

          <SocialFields
            profile={profile}
            isEditing={isEditing}
            useMobileForAllSocials={useMobileForAllSocials}
            onSocialChange={handleSocialChange}
            onUseSocialIconsChange={(v) => setProfile(prev => ({ ...prev, use_social_icons: v }))}
            onUseMobileForAllSocialsChange={(checked) => {
              setUseMobileForAllSocials(checked);
              if (checked) {
                const onlyDigits = (profile.mobile_number || '').replace(/\D/g, "");
                setProfile(prev => ({
                  ...prev,
                  social_call: onlyDigits,
                  social_telegram: onlyDigits,
                  social_whatsapp: onlyDigits,
                }));
              }
            }}
          />

          <TokenVerification
            user={user}
            metefyTokens={metefyTokens}
            onTokensUpdate={handleTokensUpdate}
            onProfileUpdate={handleProfileUpdateFromToken}
          />

          
          {/* Moved: Add your socials above token verification */}
          
          
          {isEditing && (
            <div className="flex justify-end gap-2 mt-6 mb-6">
              <Button variant="outline" onClick={() => setIsEditing(false)} className="border-border text-foreground hover:bg-muted">Cancel</Button>
              <Button type="button" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={handleSubmit}>
                <Save size={16} />
                Save
              </Button>
            </div>
          )}
        </form>
      </div>

      <Card className="bg-card border border-border shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-foreground">Profile Preview</h2>
        </div>
        <div className="text-sm text-foreground/80 mb-4">
          Here's how your profile will appear to others when they scan your metefy QR stickers or search for your metro card or metefy token.
        </div>
        <div className="flex justify-center py-4">
          <Button
            onClick={handlePreview}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Preview My Profile
          </Button>
        </div>
      </Card>


      <SearchHistory
        searchHistory={showSearchHistory ? searchHistory : []}
        isLoadingHistory={isLoadingHistory}
        totalSearches={totalSearches}
        uniqueSearches={uniqueSearches}
        showSearchHistory={showSearchHistory}
        onShowSearchHistory={handleShowSearchHistory}
      />

      <p className="text-xs text-foreground/80 mb-6">
        Note: Date, time, and total number of searches are accurate. Location is approximate and can be 5–50 km away from the actual spot.
      </p>

      <AboutOwnerProfile
        isOpen={previewDialogOpen}
        setIsOpen={setPreviewDialogOpen}
        profile={previewResult ? getOwnerProfileFromPreviewResult(previewResult) : null}
        copyTweetTemplate={() => {}}
      />

      <AvatarSelector
        isOpen={showAvatarSelector}
        onClose={() => setShowAvatarSelector(false)}
        onSelect={handleAvatarSelect}
        currentAvatarUrl={profile.photo_url}
      />

    </div>
  );
}
